"use client";

/**
 * Unified Balance Context
 *
 * Aggregates balances from all chains into a single "total available" number.
 * Composes existing hooks — does NOT add new RPC calls.
 *
 * Used by UnifiedBalanceCard and MoveFundsModal to show Robinhood-like UX.
 */

import { createContext, useContext, useMemo, useCallback, ReactNode } from "react";
import { usePolymarketBalanceContext } from "./PolymarketBalanceContext";
import { useBnbBalance } from "@/hooks/useBnbBalance";
import { useSolanaBalanceContext } from "./SolanaBalanceContext";
import { useHyperliquidBalance } from "@/hooks/useHyperliquidBalance";

interface UnifiedBalanceData {
  totalAvailable: number | null;
  polymarket: {
    usdc: number | null;
    /** USDC.e breakdown (bridged wallet balance). */
    bridged: number | null;
    /** Native USDC (Circle) sitting in the Safe before being wrapped to pUSD. */
    usdcNative: number | null;
    /** pUSD breakdown (V2 collateral, 0 pre-cutover). */
    pusd: number | null;
    portfolioValue: number | null;
  };
  predictfun: {
    usdt: number | null;
  };
  kalshi: {
    usdc: number | null;
  };
  hyperliquid: {
    /** Effective USDC balance shown in summaries — falls back to Arbitrum
     *  wallet when HL margin is empty, so users always see funds they own. */
    usdc: number | null;
  };
  /** Standalone Arbitrum USDC balance — same number as `hyperliquid` includes
   *  via fallback, but exposed separately so Move Funds can use Arbitrum as
   *  its OWN venue (bridge route) without conflating it with HL margin. */
  arbitrum: {
    usdc: number | null;
  };
  loading: boolean;
  refetch: () => void;
}

const UnifiedBalanceContext = createContext<UnifiedBalanceData>({
  totalAvailable: null,
  polymarket: { usdc: null, bridged: null, usdcNative: null, pusd: null, portfolioValue: null },
  predictfun: { usdt: null },
  kalshi: { usdc: null },
  hyperliquid: { usdc: null },
  arbitrum: { usdc: null },
  loading: false,
  refetch: () => {},
});

export function UnifiedBalanceProvider({ children }: { children: ReactNode }) {
  const {
    balance: polyBalance,
    bridgedBalance: polyBridged,
    nativeBalance: polyNativeUsdc,
    pusdBalance: polyPusd,
    portfolioValue,
    loading: polyLoading,
    refetch: refetchPoly,
  } = usePolymarketBalanceContext();

  const {
    usdtBalance: bscUsdt,
    loading: bscLoading,
    refetch: refetchBsc,
  } = useBnbBalance();

  const {
    balance: solUsdc,
    loading: solLoading,
    refetch: refetchSol,
  } = useSolanaBalanceContext();

  const {
    hlBalance,
    arbitrumBalance: hlArbBalance,
    loading: hlLoading,
    refetch: refetchHl,
  } = useHyperliquidBalance();

  // For the unified summary we want the user's actual HL spendable
  // power — that's HL margin + their Arbitrum USDC waiting to be
  // bridged. This way the wallet card shows what they really have
  // even if a chunk is mid-bridge.
  const hlEffective = (hlBalance ?? 0) + (hlArbBalance ?? 0);

  const totalAvailable = useMemo(() => {
    if (polyBalance === null && bscUsdt === null && solUsdc === null && hlBalance === null && hlArbBalance === null) return null;
    return (polyBalance || 0) + (bscUsdt || 0) + (solUsdc || 0) + hlEffective;
  }, [polyBalance, bscUsdt, solUsdc, hlBalance, hlArbBalance, hlEffective]);

  const refetch = useCallback(() => {
    refetchPoly();
    refetchBsc();
    refetchSol();
    refetchHl();
  }, [refetchPoly, refetchBsc, refetchSol, refetchHl]);

  const value = useMemo<UnifiedBalanceData>(
    () => ({
      totalAvailable,
      polymarket: {
        usdc: polyBalance,
        bridged: polyBridged,
        usdcNative: polyNativeUsdc,
        pusd: polyPusd,
        portfolioValue,
      },
      predictfun: {
        usdt: bscUsdt,
      },
      kalshi: {
        usdc: solUsdc,
      },
      hyperliquid: {
        usdc: (hlBalance == null && hlArbBalance == null) ? null : hlEffective,
      },
      arbitrum: {
        usdc: hlArbBalance,
      },
      loading: polyLoading || bscLoading || solLoading || hlLoading,
      refetch,
    }),
    [totalAvailable, polyBalance, polyBridged, polyNativeUsdc, polyPusd, portfolioValue, bscUsdt, solUsdc, hlBalance, hlArbBalance, hlEffective, polyLoading, bscLoading, solLoading, hlLoading, refetch]
  );

  return (
    <UnifiedBalanceContext.Provider value={value}>
      {children}
    </UnifiedBalanceContext.Provider>
  );
}

export function useUnifiedBalance() {
  return useContext(UnifiedBalanceContext);
}
